// Copyright 2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.
import React, { useEffect, useState } from "react";
import { AuthStatus, BrowserType } from "../../enums";
import { AuthState } from "../../types";
import BrowserFactory from "../browser/BrowserFactory";
import EmbeddedStreamer from "../browser/appStream/EmbeddedStreamer";
import log, { METRIC_NAME } from "../../logging";
import Authentication from "../authentication/Authentication";
import SignOut from "../authentication/SignOut";
import WarningAlert from "../general/WarningAlert";
import { isDeviceOnExperimentalMode } from "../../utils/userAgentUtils";
import { useTranslation } from "react-i18next";

export const App = (): JSX.Element => {
  const [authState, setAuthState] = useState<AuthState>({
    authStatus: AuthStatus.SIGNED_OUT,
    credentials: null,
  });
  const [browser, setBrowser] = useState<{
    browserType: BrowserType;
    browserEndpoint: string;
  } | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    log.publishCounterMetric(METRIC_NAME.REFRESH);

    /*
      Track page refresh events using an experimental api, this is not supported in
      iE or safari. The alternative to listen to page refresh page events will never work
      because the publish fn is async and batched so it will not finish before the browser
      kills the thread.
    */
    if (performance.getEntriesByType) {
      const perfEntries = performance.getEntriesByType("navigation");
      // eslint-disable-next-line
      // @ts-ignore spec is here https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/type
      if (perfEntries[0] && perfEntries[0].type === "reload") {
        log.publishCounterMetric(METRIC_NAME.CLICK);
      }
    }
  }, []);

  if (authState.authStatus === AuthStatus.SIGNED_IN) {
    // Valid user, render the main page

    if (!browser) {
      return (
        <BrowserFactory
          onBrowserCreation={(browserType, browserEndpoint) => {
            setBrowser({
              browserType,
              browserEndpoint,
            });
          }}
          sigv4Credentials={authState.credentials}
        />
      );
    } else {
      switch (browser.browserType) {
        case BrowserType.APPSTREAM: {
          return (
            <>
              <WarningAlert
                title={t("experimentalMode.alert.title")}
                message={t("experimentalMode.alert.message")}
                render={isDeviceOnExperimentalMode()}
              />
              <EmbeddedStreamer
                endpoint={browser.browserEndpoint}
                onSignOut={setAuthState}
              />
            </>
          );
        }
      }
    }
  } else if (authState.authStatus === AuthStatus.SIGNING_OUT) {
    return <SignOut />;
  } else {
    return <Authentication onSignIn={setAuthState} />;
  }
};

export default App;
